import { useState, useEffect } from 'react'
import { X, Ruler, Plus, Edit, Trash2, User, Calendar, FileText, Activity, Eye } from 'lucide-react'
import { Button, LoadingSpinner } from '../common'
import { MeasurementForm } from '../measurements'
import measurementService from '../../services/measurementService'
import toast from 'react-hot-toast'

function CustomerMeasurementsModal({ 
  isOpen, 
  customer, 
  onClose,
  userRole,
  onMeasurementsChange
}) {
  const [measurements, setMeasurements] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingMeasurement, setEditingMeasurement] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Use measurements from customer object if available, otherwise fetch from API
  useEffect(() => {
    if (isOpen && customer?.id) {
      // Check if customer already has measurements array
      if (customer.measurements && Array.isArray(customer.measurements)) {
        setMeasurements(customer.measurements)
        setIsLoading(false)
      } else {
        // If not, fetch from API
        fetchMeasurements()
      }
    }
  }, [isOpen, customer?.id, customer?.measurements])

  const fetchMeasurements = async () => {
    if (!customer?.id) return
    
    setIsLoading(true)
    try {
      const response = await measurementService.getAll({ 
        customerId: customer.id,
        limit: 1000 
      })
      const measurementsData = response.data?.measurements || response.measurements || []
      setMeasurements(measurementsData)
    } catch (error) {
      console.error('Error fetching measurements:', error)
      toast.error('Failed to fetch measurements')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddMeasurement = () => {
    setEditingMeasurement(null)
    setShowForm(true)
  }

  const handleEditMeasurement = (measurement) => {
    setEditingMeasurement(measurement)
    setShowForm(true)
  }

  const handleDeleteMeasurement = async (measurement) => {
    if (!window.confirm('Are you sure you want to delete this measurement?')) {
      return
    }

    try {
      await measurementService.delete(measurement.id)
      toast.success('Measurement deleted successfully!')
      await fetchMeasurements()
    } catch (error) {
      console.error('Error deleting measurement:', error)
      toast.error(error.response?.data?.message || 'Failed to delete measurement')
    }
  }

  const handleToggleStatus = async (measurement) => {
    try {
      await measurementService.toggleStatus(measurement.id)
      toast.success('Measurement status updated successfully!')
      await fetchMeasurements()
    } catch (error) {
      console.error('Error toggling measurement status:', error)
      toast.error(error.response?.data?.message || 'Failed to update measurement status')
    }
  }

  const handleSubmitMeasurement = async (data) => {
    setIsSubmitting(true)
    try {
      if (data.id) {
        // Update existing measurement
        await measurementService.update(data.id, data)
        toast.success('Measurement updated successfully!')
      } else {
        // Create new measurement - ensure customerId is set
        await measurementService.create({
          ...data,
          customerId: customer.id
        })
        toast.success('Measurement created successfully!')
      }
      setShowForm(false)
      setEditingMeasurement(null)
      // Refresh measurements - if customer has measurements array, we need to refetch
      // Otherwise, fetch from API
      if (customer.measurements && Array.isArray(customer.measurements)) {
        // If measurements are in customer object, we need to refetch customer data
        // For now, fetch from API to update the list
        await fetchMeasurements()
      } else {
        await fetchMeasurements()
      }
      // Notify parent to refresh measurements count
      if (typeof onMeasurementsChange === 'function') {
        onMeasurementsChange()
      }
    } catch (error) {
      console.error('Error saving measurement:', error)
      toast.error(error.response?.data?.message || 'Failed to save measurement')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingMeasurement(null)
  }

  if (!isOpen || !customer) return null

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Ruler className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-secondary-900">
                      Measurements - {customer.firstName} {customer.lastName}
                    </h3>
                    <p className="text-sm text-secondary-500">
                      Manage measurement records for this customer
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {measurements.length !== 1 && (
                    <Button
                      onClick={handleAddMeasurement}
                      variant="primary"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Measurement
                    </Button>
                  )}
                  <button
                    onClick={onClose}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner />
                </div>
              ) : measurements.length === 0 ? (
                <div className="text-center py-12">
                  <Ruler className="mx-auto h-12 w-12 text-secondary-400" />
                  <h3 className="mt-2 text-sm font-medium text-secondary-900">No measurements found</h3>
                  <p className="mt-1 text-sm text-secondary-500">
                    Get started by creating a new measurement record.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Measurement Type</th>
                        <th className="table-header-cell">Values</th>
                        <th className="table-header-cell">Notes</th>
                        <th className="table-header-cell">Status</th>
                        <th className="table-header-cell">Created</th>
                        <th className="table-header-cell">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {measurements.map((measurement) => (
                        <tr key={measurement.id} className="table-row">
                          <td className="table-cell">
                            <div className="flex items-center">
                              <Ruler className="h-4 w-4 mr-2 text-primary-600" />
                              <span className="text-sm font-medium text-secondary-900">
                                {measurement.measurementType || 'Standard'}
                              </span>
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="space-y-1">
                              {measurement.length && (
                                <div className="text-sm text-secondary-600">
                                  <span className="font-medium">Length:</span> {measurement.length}"
                                </div>
                              )}
                              {measurement.bust && (
                                <div className="text-sm text-secondary-600">
                                  <span className="font-medium">Bust:</span> {measurement.bust}"
                                </div>
                              )}
                              {measurement.waist && (
                                <div className="text-sm text-secondary-600">
                                  <span className="font-medium">Waist:</span> {measurement.waist}"
                                </div>
                              )}
                              {measurement.hip && (
                                <div className="text-sm text-secondary-600">
                                  <span className="font-medium">Hip:</span> {measurement.hip}"
                                </div>
                              )}
                              {measurement.shoulder && (
                                <div className="text-sm text-secondary-600">
                                  <span className="font-medium">Shoulder:</span> {measurement.shoulder}"
                                </div>
                              )}
                              {measurement.chest && (
                                <div className="text-sm text-secondary-600">
                                  <span className="font-medium">Chest:</span> {measurement.chest}"
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="text-sm text-secondary-600 max-w-xs">
                              {measurement.notes ? (
                                <div className="flex items-start">
                                  <FileText className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                                  <span className="truncate">
                                    {measurement.notes}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-secondary-400">No notes</span>
                              )}
                            </div>
                          </td>
                          <td className="table-cell">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              measurement.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {measurement.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center text-sm text-secondary-600">
                              <Calendar className="h-4 w-4 mr-2" />
                              {measurement.createdAt ? new Date(measurement.createdAt).toLocaleDateString() : 'N/A'}
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditMeasurement(measurement)}
                                className="text-primary-600 hover:text-primary-900"
                                title="Edit Measurement"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(measurement)}
                                className={measurement.isActive ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}
                                title={measurement.isActive ? 'Deactivate Measurement' : 'Activate Measurement'}
                              >
                                {measurement.isActive ? <Activity className="h-4 w-4" /> : <Ruler className="h-4 w-4" />}
                              </button>
                              {/* Only Super Admin can delete measurements */}
                              {userRole === 1 && (
                                <button
                                  onClick={() => handleDeleteMeasurement(measurement)}
                                  className="text-danger-600 hover:text-danger-900"
                                  title="Delete Measurement"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Measurement Form Modal */}
      {showForm && (
        <MeasurementForm
          isOpen={showForm}
          editingMeasurement={editingMeasurement}
          onClose={handleCloseForm}
          onSubmit={handleSubmitMeasurement}
          isLoading={isSubmitting}
          preselectedCustomerId={customer.id}
          preselectedCustomerName={`${customer.firstName} ${customer.lastName}`}
        />
      )}
    </>
  )
}

export default CustomerMeasurementsModal

